import { useQuery, useMutation, useQueryCache } from 'react-query';
import {
  getDriverThreads,
  getDriverThread,
  sendMessage,
  markMessagesAsRead,
} from '../../services/MessagingService';

/**
 * Fetches today's message thread summaries for all drivers.
 * Returns a list of thread summary objects, one per driver with messages today.
 *
 * Polls every 30 s when the tab is in the foreground (refetchIntervalInBackground: false).
 * TODO: Replace polling with a WebSocket subscription here — subscribe on mount,
 * unsubscribe on unmount, and call queryCache.setQueryData('message-threads', …)
 * with each pushed update so downstream components react without a round-trip.
 */
export const useDriverThreads = () => {
  const { data, isFetching, error, refetch } = useQuery(
    'message-threads',
    getDriverThreads,
    {
      staleTime: 0,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    }
  );

  return {
    threads: data ?? [],
    isLoading: isFetching,
    error,
    refetch,
  };
};

/**
 * Fetches the full message thread for a single driver (all-time history, not just today).
 * @param {string|number|null} driverId - Pass null to skip fetching
 *
 * Polls every 30 s when the tab is in the foreground (refetchIntervalInBackground: false).
 * TODO: Replace polling with a WebSocket subscription here — subscribe to driverId-scoped
 * events on mount, unsubscribe on unmount or when driverId changes, and push each
 * incoming message into the query cache via queryCache.setQueryData so the thread
 * updates in real time without a network round-trip.
 */
export const useDriverThread = (driverId) => {
  const { data, isFetching, error, refetch } = useQuery(
    ['message-thread', driverId],
    () => getDriverThread(driverId),
    {
      enabled: driverId != null,
      staleTime: 0,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    }
  );

  return {
    messages: data ?? [],
    isLoading: isFetching,
    error,
    refetch,
  };
};

/**
 * Mutation hook for sending a message to a driver.
 * After a successful send, invalidates the relevant thread queries so they refetch.
 *
 * Usage:
 *   const { send, isSending, error } = useSendMessage();
 *   await send({ driverId, text, type: 'direct' });
 */
export const useSendMessage = () => {
  const queryCache = useQueryCache();

  const [mutate, { isLoading: isSending, error }] = useMutation(sendMessage, {
    onSuccess: (_, variables) => {
      queryCache.invalidateQueries('message-threads');
      queryCache.invalidateQueries(['message-thread', variables.driverId]);
    },
  });

  return {
    send: mutate,
    isSending,
    error,
  };
};

/**
 * Mutation hook for marking messages as read.
 * Sends a single batch request: PATCH /deliveryops/messages/read { message_ids: [...] }
 * After success, invalidates thread queries so unread counts refresh.
 *
 * Usage:
 *   const { markAsRead, isMarking } = useMarkAsRead();
 *   await markAsRead({ driverId, messageIds: ['id1', 'id2'] });
 */
export const useMarkAsRead = () => {
  const queryCache = useQueryCache();

  const [mutate, { isLoading: isMarking, error }] = useMutation(
    ({ messageIds }) => markMessagesAsRead(messageIds),
    {
      onSuccess: (_, variables) => {
        queryCache.invalidateQueries('message-threads');
        if (variables.driverId) {
          queryCache.invalidateQueries(['message-thread', variables.driverId]);
        }
      },
    }
  );

  return {
    markAsRead: mutate,
    isMarking,
    error,
  };
};
